import { Injectable, inject } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SentenceCasePipe } from '../pipes/sentence-case.pipe';
import { TitleCasePipe } from '@angular/common';
import { Locales } from '../constants/locales.config';

export const localeLanguages: Map<string, string> = new Map([
  ['en', 'en-US'],
  ['vi', 'vi-VN'],
]);

@Injectable({
  providedIn: 'root',
})
export class CustomTranslateService {
  sentenceCasePipe = inject(SentenceCasePipe);
  titleCasePipe = inject(TitleCasePipe);

  private languageSubject = new BehaviorSubject<any>(null);
  language$: Observable<string> = this.languageSubject.asObservable();
  private defaultLocale: string = 'en-US';

  private translateService = inject(TranslateService);

  constructor() {
    this.initialization();
  }

  private initialization() {
    const lang = localStorage.getItem('lang');
    this.translateService.setDefaultLang(
      lang && Locales.includes(lang) ? lang : 'en',
    );
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.languageSubject.next(event.lang);
    });
    this.translateService.use(lang ?? 'en');
  }

  getLocale(): string {
    return localeLanguages.get(this.translateService.currentLang) || this.defaultLocale;
  }

  setLanguage(lang: string) {
    localStorage.setItem('lang', lang);
    try {
      this.translateService.use(lang);
    } catch (error) {
      console.warn('Cannot translate this language, apply English as default');
      this.translateService.use(this.translateService.getDefaultLang());
    }
  }

  getCurrentLanguage(): string {
    return (
      localStorage.getItem('lang') ||
      this.translateService.getBrowserLang() ||
      'en'
    );
  }

  transform(key: string): string {
    return this.translateService.instant(key) as string;
  }

  sentenceCase(value: string): string {
    value = this.transform(value);
    return this.sentenceCasePipe.transform(value);
  }

  titleCase(value: string): string {
    value = this.transform(value);
    return this.titleCasePipe.transform(value);
  }

  translateWithValue(
    key: string,
    params: { value: string; style?: string },
  ): string {
    const translatedValue = this.translateService.instant(key);
    const value = params.value;
    const style = params.style || '';
    return `<span>${translatedValue} <span class="${style}">${value}</span>?</span>`;
  }
}
